import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation } from '@apollo/react-hooks'
import { toastr } from 'react-redux-toastr'
import { LoaderCircle, Plus, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Toggle } from '@/components/ui/toggle'
import * as Query from '../../../api/gql/query'
import * as Mutation from '../../../api/gql/mutation'
import IsoDatePicker from './IsoDatePicker'
import { describeScheduleError } from '../lib/errors'
import { todayIso } from '../lib/format'
import type { ScheduleCalendar, ScheduleCalendarException } from '../types'

interface Props { open: boolean; onOpenChange: (open: boolean) => void; projectId: string; calendar?: ScheduleCalendar; onSaved?: () => void }
const WEEKDAYS = [1, 2, 3, 4, 5, 6, 7]

const CalendarDialog: React.FC<Props> = ({ open, onOpenChange, projectId, calendar, onSaved }) => {
  const { t } = useTranslation()
  const [name, setName] = useState('')
  const [days, setDays] = useState<number[]>([1, 2, 3, 4, 5])
  const [exceptions, setExceptions] = useState<ScheduleCalendarException[]>([])
  const [error, setError] = useState<string | null>(null)
  const [upsert, { loading }] = useMutation(Mutation.UPSERT_SCHEDULE_CALENDAR, { refetchQueries: [{ query: Query.SCHEDULE_CALENDARS, variables: { projectId } }] })

  useEffect(() => {
    if (!open) return
    setName(calendar?.name ?? '')
    setDays(calendar?.workingWeekdays ?? [1, 2, 3, 4, 5])
    setExceptions(calendar?.exceptions.map((e) => ({ ...e })) ?? [])
    setError(null)
  }, [open, calendar])

  const valid = name.trim().length > 0 && days.length > 0 && exceptions.every((e) => e.fromDate && e.toDate && e.fromDate <= e.toDate)

  const save = async () => {
    if (!valid) return
    try {
      await upsert({ variables: { projectId, input: { id: calendar?.id, name: name.trim(), workingWeekdays: [...days].sort(), exceptions: exceptions.map((e) => ({ name: e.name || null, fromDate: e.fromDate, toDate: e.toDate, working: e.working })) } } })
      toastr.success('', t('Schedule_calendar_saved'))
      onSaved?.()
      onOpenChange(false)
    } catch (e: any) {
      setError(describeScheduleError(e.message, t))
    }
  }
  const updateException = (i: number, patch: Partial<ScheduleCalendarException>) => setExceptions((xs) => xs.map((x, j) => (j === i ? { ...x, ...patch } : x)))

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next && !loading) onOpenChange(false) }}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader><DialogTitle>{calendar ? t('Schedule_calendar_edit') : t('Schedule_calendar_new')}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="cal-name">{t('Schedule_field_name')}</Label>
            <Input id="cal-name" value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div className="space-y-2">
            <Label>{t('Schedule_calendar_working_days')}</Label>
            <div className="flex gap-1">
              {WEEKDAYS.map((d) => (
                <Toggle key={d} size="sm" pressed={days.includes(d)} onPressedChange={(on) => setDays((xs) => (on ? [...xs, d] : xs.filter((x) => x !== d)))} aria-label={t(`Schedule_weekday_${d}`)}>
                  {t(`Schedule_weekday_${d}`)}
                </Toggle>
              ))}
            </div>
            {days.length === 0 && <p className="text-xs text-destructive">{t('Schedule_calendar_need_working_day')}</p>}
          </div>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>{t('Schedule_calendar_exceptions')}</Label>
              <Button type="button" variant="outline" size="sm" className="h-8" onClick={() => setExceptions((xs) => [...xs, { name: '', fromDate: todayIso(), toDate: todayIso(), working: false }])}><Plus className="h-4 w-4" />{t('Schedule_calendar_add_exception')}</Button>
            </div>
            {exceptions.length === 0 ? <p className="text-sm text-muted-foreground">{t('Schedule_calendar_no_exceptions')}</p> : (
              <div className="space-y-2">
                {exceptions.map((ex, i) => (
                  <div key={i} className="grid grid-cols-[1fr_150px_150px_auto_auto] items-center gap-2">
                    <Input value={ex.name ?? ''} placeholder={t('Schedule_calendar_exception_name')} onChange={(e) => updateException(i, { name: e.target.value })} className="h-8" />
                    <IsoDatePicker size="sm" value={ex.fromDate} onChange={(v) => updateException(i, { fromDate: v ?? ex.fromDate })} />
                    <IsoDatePicker size="sm" value={ex.toDate} onChange={(v) => updateException(i, { toDate: v ?? ex.toDate })} />
                    <div className="flex items-center gap-1 text-xs"><Switch checked={ex.working} onCheckedChange={(v) => updateException(i, { working: v })} />{t(ex.working ? 'Schedule_calendar_working' : 'Schedule_calendar_non_working')}</div>
                    <Button type="button" variant="ghost" size="icon" className="h-8 w-8" onClick={() => setExceptions((xs) => xs.filter((_, j) => j !== i))}><Trash2 className="h-4 w-4" /></Button>
                  </div>
                ))}
              </div>
            )}
          </div>
          {error && <p className="text-sm text-destructive">{error}</p>}
          <div className="flex items-center justify-end gap-2 pt-4 border-t">
            <Button variant="outline" onClick={() => onOpenChange(false)} disabled={loading}>{t('Common_cancel')}</Button>
            <Button onClick={save} disabled={!valid || loading}>{loading && <LoaderCircle className="h-4 w-4 animate-spin" />}{t('Common_save')}</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
export default CalendarDialog
